/**
 * Task 9 / A1 — which organisation the suppression screens are acting on, and whether that is
 * somebody else's.
 *
 * WHY THIS IS ITS OWN MODULE, and why it has no imports.
 *
 * Suppression scope used to come from the CloudCoverToken's OI claim — the LOGIN organisation —
 * while every other screen in this SPA scopes on the separately selected MANAGED organisation.
 * The token is not reissued when an administrator switches organisation, so a reseller or master
 * administrator silently read and wrote against their own organisation instead of the customer's.
 * The organisation now travels to the API as a request header, which means resolving it correctly
 * is load-bearing rather than cosmetic, and therefore has to be testable.
 *
 * NavBar.js cannot be imported outside a browser (ag-grid, msal, bootstrap CSS and a 2.7MB
 * Authentication-Context) and ClientApp/node_modules is not checked in, so logic placed in a
 * component would be unexecutable in a test. Out here, with the pure functions taking their inputs
 * explicitly, it runs under bare node. Same rationale as Helpers/SuppressionDuration.js; the
 * harness is Task9Verification/Harnesses/suppressionorg.
 *
 * Keep this module free of imports.
 */

/** sessionStorage keys, written by RegCheck.js (login) and the two organisation-picker grids. */
const MANAGED_ORG_ID_KEY = "ManageOrgId";
const MANAGED_ORG_NAME_KEY = "ManageOrgName";

/**
 * Query parameter first, sessionStorage second.
 *
 * This is the platform's convention — Pages/Settings.js repeats it about a dozen times — and the
 * precedence is the important half. NavBar.js:34-35 hydrates from the URL first, so a page that
 * consulted only sessionStorage would scope its DATA to a stale organisation while the navigation
 * around it pointed at the one in the URL. That is the state a pasted `?orgId=` link arrives in.
 */
function fromSearchOrStorage(search, storage, param, key) {
    let fromUrl = null;
    try {
        fromUrl = new URLSearchParams(search || "").get(param);
    } catch (e) {
        fromUrl = null;
    }
    if (fromUrl && fromUrl.trim()) return fromUrl.trim();

    let stored = null;
    try {
        stored = storage ? storage.getItem(key) : null;
    } catch (e) {
        // Access to storage can throw outright in some privacy modes; an absent organisation is a
        // better outcome than a page that will not render.
        stored = null;
    }
    return stored && stored.trim() ? stored.trim() : null;
}

/** The organisation whose suppressions are being viewed or changed. Null when none is selected. */
export function resolveManagedOrgId(search, storage) {
    return fromSearchOrStorage(search, storage, "orgId", MANAGED_ORG_ID_KEY);
}

/**
 * Its display name. Resolved independently of the id on purpose: a link may legitimately carry one
 * and not the other, and a missing name must never suppress a correctly resolved id.
 */
export function resolveManagedOrgName(search, storage) {
    return fromSearchOrStorage(search, storage, "orgName", MANAGED_ORG_NAME_KEY);
}

/**
 * Normalises an organisation id for COMPARISON only.
 *
 * The platform stores GUIDs in both plain and braced form and Guid.TryParse accepts either, so two
 * spellings of the same organisation must not read as different ones.
 */
function normaliseOrgId(value) {
    if (!value || typeof value !== "string") return null;
    const trimmed = value.trim().replace(/^\{|\}$/g, "").toLowerCase();
    return trimmed.length ? trimmed : null;
}

/**
 * The LOGIN organisation, read from the CloudCoverToken's OI claim.
 *
 * DISPLAY ONLY. This decodes without verifying, so it must never inform an access decision — the
 * server re-derives the login organisation from the signed token and is the only thing that
 * decides anything. It is used here to answer one cosmetic question: is the organisation on screen
 * somebody else's? Same client-side decode already used by Store/Authentication-Context.js to
 * check expiry.
 *
 * Returns null for anything malformed rather than throwing; a missing banner is an acceptable
 * outcome, a crashed page is not.
 */
export function decodeLoginOrgId(cloudCoverToken) {
    if (!cloudCoverToken || typeof cloudCoverToken !== "string") return null;

    const parts = cloudCoverToken.split(".");
    if (parts.length < 2 || !parts[1]) return null;

    try {
        // JWTs are base64URL: restore the standard alphabet and the padding atob expects.
        let segment = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        while (segment.length % 4 !== 0) segment += "=";

        const json = typeof atob === "function"
            ? atob(segment)
            // Node before 16 has no atob; the harness runs there and the browser never takes this
            // branch, so Buffer is referenced only behind the guard.
            : Buffer.from(segment, "base64").toString("binary");

        const payload = JSON.parse(json);
        const orgId = payload && payload.OI;
        return orgId && String(orgId).trim() ? String(orgId).trim() : null;
    } catch (e) {
        return null;
    }
}

/**
 * True only when a DIFFERENT organisation is being managed.
 *
 * Both sides must be known: an unknown login organisation means the question cannot be answered,
 * and answering "yes" on a guess would put a banner in front of every plain organisation
 * administrator.
 */
export function isActingOnBehalf(loginOrgId, managedOrgId) {
    const login = normaliseOrgId(loginOrgId);
    const managed = normaliseOrgId(managedOrgId);
    if (!login || !managed) return false;
    return login !== managed;
}

/**
 * Builds a link between the suppression pages that keeps the managed organisation.
 *
 * NO TRAILING SLASH, ever. package.json sets "homepage": "./", so the production build emits
 * relative asset URLs that resolve against the URL's DIRECTORY. `/ErrorSuppression?orgId=…` loads
 * assets from `/static/…`; `/ErrorSuppression/?orgId=…` asks for `/ErrorSuppression/static/…`,
 * app.UseSpa answers with index.html, and the page renders blank. React-router still matches the
 * route, so this fails only on a fresh load, a refresh or a pasted link — which is exactly how the
 * client encounters it.
 */
export function suppressionPageUrl(path, orgId, orgName) {
    const base = String(path || "").replace(/\/+$/, "");

    const params = new URLSearchParams();
    if (orgId && String(orgId).trim()) params.set("orgId", String(orgId).trim());
    if (orgName && String(orgName).trim()) params.set("orgName", String(orgName).trim());

    const query = params.toString();
    return query ? `${base}?${query}` : base;
}

// ---------------------------------------------------------------------------------------------
// Browser wrappers. Thin on purpose: everything worth testing is above, and these only supply the
// two globals a component would otherwise reach for inline.
// ---------------------------------------------------------------------------------------------

/** The managed organisation for the current page, as { orgId, orgName }. */
export function currentManagedOrg() {
    const search = typeof window !== "undefined" ? window.location.search : "";
    const storage = typeof sessionStorage !== "undefined" ? sessionStorage : null;
    return {
        orgId: resolveManagedOrgId(search, storage),
        orgName: resolveManagedOrgName(search, storage)
    };
}

/** The signed-in user's own organisation. Display only — see decodeLoginOrgId. */
export function currentLoginOrgId() {
    try {
        return decodeLoginOrgId(sessionStorage.getItem("CloudCoverToken"));
    } catch (e) {
        return null;
    }
}

/** Whether this page is showing another organisation's suppressions. */
export function currentlyActingOnBehalf() {
    return isActingOnBehalf(currentLoginOrgId(), currentManagedOrg().orgId);
}

/** Builds a suppression link carrying the organisation currently in context. */
export function currentSuppressionPageUrl(path) {
    const { orgId, orgName } = currentManagedOrg();
    return suppressionPageUrl(path, orgId, orgName);
}
